import type { GameEvent } from '../schema';

export const FAMILY_EVENTS: GameEvent[] = [
  {
    id: 'family.childQuestion',
    title: 'Children?',
    description:
      'The conversation has arrived. It is a decision about money, time, career, and the shape of the next twenty years.',
    category: 'family',
    pools: ['pool.family', 'pool.romance.commit', 'pool.social'],
    weight: 9,
    cooldown: 4,
    requires: [
      { k: 'marital', anyOf: ['married', 'partnered'] },
      { k: 'age', min: 24, max: 45 },
    ],
    weightMods: [
      { conditions: [{ k: 'aspiration', defId: 'dream.family', status: 'active' }], multiplier: 2.2 },
      { conditions: [{ k: 'age', min: 30, max: 39 }], multiplier: 1.6 },
      { conditions: [{ k: 'num', path: 'childCount', op: '>=', value: 2 }], multiplier: 0.35 },
    ],
    choices: [
      {
        id: 'yes',
        label: 'Have a child',
        hint: 'mixed',
        tags: ['family', 'commitment'],
        outcomes: [
          {
            weight: 82,
            label: 'A child arrived',
            narrative: 'Everything reorganised itself around a person who weighs three kilograms.',
            tone: 'positive',
            effects: [
              { k: 'family', op: 'childBorn' },
              { k: 'stat', stat: 'purpose', delta: 18 },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'stat', stat: 'energy', delta: -16 },
              { k: 'stat', stat: 'stress', delta: 12 },
              { k: 'habit', habit: 'sleep', delta: -18 },
              { k: 'aspiration', op: 'progress', defId: 'dream.family', delta: 100 },
              { k: 'schedule', afterYears: 5, label: 'School years begin', eventId: 'family.schoolYears', effects: [] },
            ],
          },
          {
            weight: 18,
            label: 'It did not happen easily',
            narrative: 'Two years of hope, appointments, and a grief nobody prepares you for.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -3, note: 'fertility treatment' },
              { k: 'stat', stat: 'happiness', delta: -14 },
              { k: 'stat', stat: 'stress', delta: 16 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: 8 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: 10 },
              { k: 'triggerEvent', eventId: 'family.fertilityFork', afterYears: 2 },
            ],
          },
        ],
      },
      {
        id: 'wait',
        label: 'Wait a few years',
        detail: 'Career first. Money first. Readiness first.',
        hint: 'mixed',
        tags: ['career'],
        defers: { id: 'int.children', label: 'Decide about children', topic: 'family', urgency: 28, resumeEventId: 'family.childQuestion', decayEventId: 'family.windowClosed' },
        outcomes: [
          {
            weight: 1,
            label: 'Postponed',
            narrative: 'Defensible. Biology is not interested in your reasoning.',
            tone: 'neutral',
            effects: [{ k: 'inertia', op: 'pressure', delta: 4 }],
          },
        ],
      },
      {
        id: 'no',
        label: 'Decide against it',
        hint: 'safe',
        tags: ['freedom'],
        outcomes: [
          {
            weight: 1,
            label: 'Chose not to',
            narrative: 'A deliberate decision, made together, and revisited less often than people assume.',
            tone: 'neutral',
            effects: [
              { k: 'flag', flag: 'choseNoChildren', value: true },
              { k: 'aspiration', op: 'resolve', defId: 'dream.family', status: 'abandoned' },
              { k: 'stat', stat: 'energy', delta: 8 },
              { k: 'stat', stat: 'happiness', delta: 4 },
              { k: 'habit', habit: 'saving', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.windowClosed',
    title: 'The decision made itself',
    description: 'You kept postponing it until it stopped being a choice.',
    category: 'family',
    pools: ['pool.inertia'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'ack',
        label: 'Sit with it',
        outcomes: [
          {
            weight: 1,
            label: 'The window closed',
            narrative: 'Not a decision you made. A decision you allowed.',
            tone: 'negative',
            effects: [
              { k: 'aspiration', op: 'resolve', defId: 'dream.family', status: 'failed' },
              { k: 'regret', op: 'add', id: 'regret.children', label: 'Postponed children until it was too late', weight: 30 },
              { k: 'stat', stat: 'happiness', delta: -10 },
              { k: 'memory', title: 'The family that never happened', tone: 'negative', category: 'family', weight: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.fertilityFork',
    title: 'Another route',
    description: 'There are other ways to build a family. All of them cost money, time, and certainty.',
    category: 'family',
    pools: ['pool.family'],
    weight: 0,
    choices: [
      {
        id: 'adopt',
        label: 'Adopt',
        hint: 'slow',
        tags: ['family', 'courage'],
        outcomes: [
          {
            weight: 74,
            label: 'A child came home',
            narrative: 'Eighteen months of assessment, and then a person walked into your house and stayed.',
            tone: 'positive',
            effects: [
              { k: 'family', op: 'childBorn' },
              { k: 'cashIncomeMonths', months: -2, note: 'adoption process' },
              { k: 'stat', stat: 'purpose', delta: 22 },
              { k: 'stat', stat: 'happiness', delta: 16 },
              { k: 'aspiration', op: 'progress', defId: 'dream.family', delta: 100 },
              { k: 'achievement', id: 'ach.adopt', label: 'Adopted a child', category: 'family' },
            ],
          },
          {
            weight: 26,
            label: 'It fell through',
            narrative: 'A process that treats hope as a renewable resource.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -1.5, note: 'adoption costs' },
              { k: 'stat', stat: 'happiness', delta: -12 },
              { k: 'trait', trait: 'resilience', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'accept',
        label: 'Build a different life',
        hint: 'safe',
        tags: ['acceptance'],
        outcomes: [
          {
            weight: 1,
            label: 'Made peace with it',
            narrative: 'You stopped grieving a life you did not have and started liking the one you did.',
            tone: 'neutral',
            effects: [
              { k: 'aspiration', op: 'resolve', defId: 'dream.family', status: 'abandoned' },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: 12 },
              { k: 'counter', name: 'travelTrips', delta: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.schoolYears',
    title: 'Small people, large logistics',
    description: 'Drop-offs, illnesses, and a calendar that belongs to someone else. Work has not adjusted.',
    category: 'family',
    pools: ['pool.family'],
    weight: 8,
    cooldown: 3,
    requires: [{ k: 'hasPerson', kind: 'child' }],
    choices: [
      {
        id: 'scaleBack',
        label: 'Scale back at work',
        detail: 'Four days, or a less demanding role.',
        hint: 'costly',
        tags: ['family', 'balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Went part-time',
            narrative: 'Less money, fewer promotions, and you were there for most of it.',
            tone: 'positive',
            effects: [
              { k: 'salary', mode: 'mult', value: 0.82 },
              { k: 'career', op: 'adjust', field: 'workload', delta: -16 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -6 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 18 },
              { k: 'family', op: 'adjustHarmony', delta: 12 },
              { k: 'stat', stat: 'stress', delta: -8 },
              { k: 'burnout', op: 'load', delta: -10 },
            ],
          },
        ],
      },
      {
        id: 'payForHelp',
        label: 'Pay for help',
        hint: 'mixed',
        requires: [{ k: 'num', path: 'income', op: '>=', value: 70000 }],
        showWhenLocked: true,
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Hired help',
            narrative: 'Expensive, effective, and quietly complicated to feel good about.',
            tone: 'neutral',
            effects: [
              { k: 'fixedExpenses', mode: 'add', value: 13000 },
              { k: 'stat', stat: 'stress', delta: -8 },
              { k: 'stat', stat: 'energy', delta: 7 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 3 },
            ],
          },
        ],
      },
      {
        id: 'absorb',
        label: 'Absorb it',
        detail: 'Do both. Sleep less.',
        hint: 'risky',
        tags: ['sacrifice'],
        outcomes: [
          {
            weight: 1,
            label: 'Did everything',
            narrative: 'You held it together for years. The cost showed up on a different page of the ledger.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'energy', delta: -14 },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'burnout', op: 'load', delta: 16 },
              { k: 'habit', habit: 'exercise', delta: -10 },
              { k: 'habit', habit: 'sleep', delta: -10 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.teenTrouble',
    title: 'Your teenager is in trouble',
    description: 'School has called. This is not a phase to be managed remotely.',
    category: 'family',
    pools: ['pool.family.teen'],
    weight: 7,
    cooldown: 4,
    requires: [{ k: 'hasPerson', kind: 'child' }, { k: 'age', min: 36 }],
    weightMods: [
      { conditions: [{ k: 'num', path: 'householdHarmony', op: '<', value: 45 }], multiplier: 1.8 },
      { conditions: [{ k: 'num', path: 'workload', op: '>=', value: 72 }], multiplier: 1.5 },
    ],
    choices: [
      {
        id: 'showUp',
        label: 'Drop everything',
        hint: 'costly',
        tags: ['family'],
        outcomes: [
          {
            weight: 1,
            label: 'You were there',
            narrative: 'Six months of appointments and difficult evenings. They came out the other side.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 22 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 16 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -8 },
              { k: 'stat', stat: 'stress', delta: 12 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'family', op: 'adjustHarmony', delta: 10 },
            ],
          },
        ],
      },
      {
        id: 'professional',
        label: 'Get professional help',
        hint: 'mixed',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 64,
            label: 'It helped',
            narrative: 'Someone qualified did what you could not. That is not a failure.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -1.4, note: 'therapy' },
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 12 },
              { k: 'family', op: 'adjustHarmony', delta: 8 },
            ],
          },
          {
            weight: 36,
            label: 'They refused to engage',
            narrative: 'You paid for four sessions they did not attend.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -0.8, note: 'therapy' },
              { k: 'relationshipAll', kind: 'child', dim: 'conflict', delta: 16 },
              { k: 'stat', stat: 'stress', delta: 12 },
            ],
          },
        ],
      },
      {
        id: 'delegate',
        label: 'Leave it to your partner',
        hint: 'risky',
        requires: [{ k: 'marital', anyOf: ['married', 'partnered'] }],
        showWhenLocked: true,
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed at work',
            narrative: 'The crisis was handled. Everyone remembers who handled it.',
            tone: 'negative',
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: 20 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: -14 },
              { k: 'family', op: 'adjustHarmony', delta: -12 },
              { k: 'regret', op: 'add', id: 'regret.absent', label: 'Was not there when your child needed you', weight: 24 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.parentDecline',
    title: 'Your parent needs help',
    description: 'The independence has gone. Someone has to organise the rest of their life.',
    category: 'family',
    pools: ['pool.family.parents'],
    weight: 8,
    cooldown: 5,
    requires: [{ k: 'hasPerson', kind: 'parent' }, { k: 'age', min: 38 }],
    choices: [
      {
        id: 'careSelf',
        label: 'Take it on yourself',
        hint: 'costly',
        tags: ['family', 'sacrifice'],
        outcomes: [
          {
            weight: 1,
            label: 'Became the carer',
            narrative: 'Years of it. Unpaid, unseen, and the right thing by any measure you believe in.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: 22 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: 18 },
              { k: 'stat', stat: 'energy', delta: -16 },
              { k: 'stat', stat: 'stress', delta: 18 },
              { k: 'burnout', op: 'load', delta: 16 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -8 },
              { k: 'stat', stat: 'purpose', delta: 10 },
              { k: 'counter', name: 'contributions', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'payCare',
        label: 'Pay for professional care',
        hint: 'costly',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Arranged care',
            narrative: 'Good care is expensive and the guilt is free.',
            tone: 'neutral',
            effects: [
              { k: 'fixedExpenses', mode: 'add', value: 16000 },
              { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: 8 },
              { k: 'stat', stat: 'stress', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'share',
        label: 'Split it with siblings',
        hint: 'mixed',
        requires: [{ k: 'hasPerson', kind: 'sibling' }],
        showWhenLocked: true,
        tags: ['family'],
        outcomes: [
          {
            weight: 55,
            label: 'They stepped up',
            narrative: 'A rota, a shared account, and a family that worked better under pressure than expected.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 58 }], multiplier: 1.7 }],
            effects: [
              { k: 'relationshipAll', kind: 'sibling', dim: 'closeness', delta: 14 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: 12 },
              { k: 'stat', stat: 'stress', delta: 8 },
              { k: 'cashIncomeMonths', months: -1, note: 'care contribution' },
            ],
          },
          {
            weight: 45,
            label: 'It fell on you anyway',
            narrative: 'Everyone agreed in principle. In practice, you had the car and the free weekends.',
            tone: 'negative',
            effects: [
              { k: 'relationshipAll', kind: 'sibling', dim: 'conflict', delta: 22 },
              { k: 'stat', stat: 'stress', delta: 16 },
              { k: 'stat', stat: 'energy', delta: -12 },
              { k: 'burnout', op: 'load', delta: 12 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.parentDeath',
    title: 'A parent dies',
    description: 'Expected or not, it reorganises where you stand in your own family.',
    category: 'family',
    pools: ['pool.family.parents', 'pool.family.adult'],
    weight: 5,
    cooldown: 6,
    requires: [{ k: 'hasPerson', kind: 'parent' }, { k: 'age', min: 40 }],
    weightMods: [{ conditions: [{ k: 'age', min: 58 }], multiplier: 2.2 }],
    choices: [
      {
        id: 'grieve',
        label: 'Take the time',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Grieved properly',
            narrative: 'Three weeks off, a great deal of admin, and the slow work of being the older generation now.',
            tone: 'negative',
            effects: [
              { k: 'person', op: 'remove', who: { r: 'kind', kind: 'parent', pick: 'weakest' }, reason: 'died' },
              { k: 'stat', stat: 'happiness', delta: -18 },
              { k: 'stat', stat: 'purpose', delta: 4 },
              { k: 'cash', amount: 24000 },
              { k: 'relationshipAll', kind: 'sibling', dim: 'closeness', delta: 10 },
              { k: 'memory', title: 'Lost a parent', tone: 'negative', category: 'family', weight: 10 },
              { k: 'schedule', afterYears: 2, label: 'Grief settles', effects: [{ k: 'stat', stat: 'happiness', delta: 10 }] },
            ],
          },
        ],
      },
      {
        id: 'work',
        label: 'Bury it in work',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Did not stop',
            narrative: 'You were back in the office on Tuesday and fine for about four years.',
            tone: 'negative',
            effects: [
              { k: 'person', op: 'remove', who: { r: 'kind', kind: 'parent', pick: 'weakest' }, reason: 'died' },
              { k: 'stat', stat: 'happiness', delta: -12 },
              { k: 'cash', amount: 24000 },
              { k: 'habit', habit: 'workaholism', delta: 12 },
              { k: 'schedule', afterYears: 4, label: 'Unprocessed grief', effects: [{ k: 'stat', stat: 'happiness', delta: -10 }, { k: 'burnout', op: 'load', delta: 14 }, { k: 'stat', stat: 'stress', delta: 10 }] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.adultChildren',
    title: 'They are adults now',
    description: 'Your children have opinions about how you raised them, and lives that no longer require you.',
    category: 'family',
    pools: ['pool.family.adult'],
    weight: 7,
    cooldown: 5,
    requires: [{ k: 'hasPerson', kind: 'child' }, { k: 'age', min: 48 }],
    choices: [
      {
        id: 'support',
        label: 'Help them financially',
        hint: 'costly',
        requires: [{ k: 'num', path: 'netWorth', op: '>=', value: 80000 }],
        showWhenLocked: true,
        tags: ['family', 'generosity'],
        outcomes: [
          {
            weight: 1,
            label: 'Gave them a start',
            narrative: 'A deposit, a loan that was always a gift. It mattered enormously to them.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -8, note: 'helping the children' },
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 16 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 10 },
              { k: 'stat', stat: 'purpose', delta: 10 },
              { k: 'counter', name: 'contributions', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'presence',
        label: 'Just be available',
        hint: 'safe',
        tags: ['family'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed close',
            narrative: 'Sunday calls, occasional visits, and no advice unless asked. Harder than it sounds.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 14 },
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 8 },
              { k: 'stat', stat: 'happiness', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'distance',
        label: 'Let them get on with it',
        hint: 'mixed',
        outcomes: [
          {
            weight: 1,
            label: 'Stepped back',
            narrative: 'Respectful, and slightly further away every year.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: -8 },
              { k: 'stat', stat: 'energy', delta: 4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'family.grandchild',
    title: 'A grandchild',
    description: 'A second chance at the parts of parenting you enjoyed, with none of the responsibility.',
    category: 'family',
    pools: ['pool.retired.family', 'pool.family.adult'],
    weight: 6,
    cooldown: 4,
    requires: [{ k: 'hasPerson', kind: 'child' }, { k: 'age', min: 55 }],
    choices: [
      {
        id: 'involved',
        label: 'Be properly involved',
        hint: 'safe',
        tags: ['family', 'legacy'],
        outcomes: [
          {
            weight: 1,
            label: 'A grandparent',
            narrative: 'Two days a week of childcare and the best years of your late life.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 18 },
              { k: 'stat', stat: 'purpose', delta: 14 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 14 },
              { k: 'counter', name: 'contributions', delta: 1 },
              { k: 'stat', stat: 'energy', delta: -6 },
              { k: 'memory', title: 'Became a grandparent', tone: 'milestone', category: 'family', weight: 9 },
            ],
          },
        ],
      },
      {
        id: 'occasional',
        label: 'Visit, but keep your life',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'A fond distance',
            narrative: 'Birthdays and holidays. Warm, and not central.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 9 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 5 },
            ],
          },
        ],
      },
    ],
  },
];
